// TODO: migrate to BlockDividerButton (src/components/block-divider-button/BlockDividerButton.tsx)
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Group,
  IconPlusCircle,
  Input,
  Loader,
  Overlay,
  Tooltip
} from '@screentone/core';

import { useAlert } from 'contexts/alert/useAlert';
import { MobileIssueDtoArtifactFragment, MobileIssueSectionContainer } from 'data/generated/graphql';
import { useAllessehCollectionSummariesQuery } from 'hooks/useAllessehCollectionSummariesQuery';
import { getCollectionIdPrefix } from 'utils/text';
import styles from './AddSectionButton.module.scss';
import { convertSummariesToSectionData, normalizeCollectionIdInput } from './AddSectionButton.utils';

interface AddSectionButtonProps {
  nextSectionIndex: number;
  sectionIndex: number;
  issue: MobileIssueDtoArtifactFragment;
  onAddSectionFromCollection?: (payload: { sectionIndex: number; sectionData: MobileIssueSectionContainer }) => void;
}

export const AddSectionButton = ({
  nextSectionIndex,
  sectionIndex,
  issue,
  onAddSectionFromCollection
}: AddSectionButtonProps) => {
  const { alertError } = useAlert();
  const collectionIdPrefix = getCollectionIdPrefix();
  const [collectionId, setCollectionId] = useState(collectionIdPrefix);
  const [submittedCollectionId, setSubmittedCollectionId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleDismissModal = () => setIsModalOpen(false);

  const {
    data: collectionIdData,
    isLoading: isLoadingCollectionIdData,
    isFetching: isFetchingCollectionIdData,
    error: collectionIdError
  } = useAllessehCollectionSummariesQuery(submittedCollectionId, {
    enabled: submittedCollectionId !== '',
    staleTime: 30_000
  });

  const handleCollectionIdChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newCollectionId = normalizeCollectionIdInput(e.target.value, collectionId, collectionIdPrefix);
    if (issue.root.collection.filter((section) => section.allessehId === newCollectionId).length > 0) {
      setErrorMessage('This collection is already added to the issue.');
    } else {
      setErrorMessage('');
    }
    setCollectionId(newCollectionId);
  };

  const handleAddSection = () => {
    setSubmittedCollectionId(collectionId);
  };

  useEffect(() => {
    if (collectionIdError) {
      setSubmittedCollectionId('');
      setCollectionId(collectionIdPrefix);
      alertError((collectionIdError as { response?: { data?: { message?: string } } }).response?.data?.message);
    }
  }, [collectionIdError, alertError, collectionIdPrefix]);

  useEffect(() => {
    if ((submittedCollectionId && !collectionIdData) || isFetchingCollectionIdData || !onAddSectionFromCollection)
      return;

    setSubmittedCollectionId('');
    setCollectionId(collectionIdPrefix);
    setIsModalOpen(false);
    if (collectionIdData) {
      onAddSectionFromCollection({
        sectionIndex,
        sectionData: convertSummariesToSectionData(collectionIdData)
      });
    }
  }, [
    collectionIdData,
    collectionIdPrefix,
    isFetchingCollectionIdData,
    isLoadingCollectionIdData,
    onAddSectionFromCollection,
    sectionIndex,
    submittedCollectionId
  ]);

  return (
    <div className={styles.blockDivider}>
      <div className={styles.blockDividerContent}>
        <Divider className={styles.blockDividerDivider} />
        <Tooltip>
          <Tooltip.Content position={nextSectionIndex === 0 ? 'bottom' : 'top'}>Add a Section Here</Tooltip.Content>
          <Tooltip.Trigger>
            <Button tertiary icon={IconPlusCircle} onClick={handleOpenModal} data-testid="add-section-button" />
          </Tooltip.Trigger>
        </Tooltip>
        <Overlay onDismiss={handleDismissModal} status={isModalOpen ? 'open' : 'closed'} className={styles.overlay}>
          <Box>
            <Box.Title>ADD SECTION BY COLLECTION ID</Box.Title>
            <Box.Content>
              <FormHelperText>Input valid Section ID here</FormHelperText>
              <Input type="text" value={collectionId} onChange={handleCollectionIdChange} />
              {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
              <Group className={styles.buttonGroup} margin={{ top: 'md' }}>
                <Button
                  primary
                  onClick={handleAddSection}
                  disabled={collectionId === collectionIdPrefix || !!errorMessage || isFetchingCollectionIdData}
                >
                  {isFetchingCollectionIdData ? <Loader size="md" /> : 'Add section'}
                </Button>
                <Button secondary onClick={handleDismissModal} disabled={isFetchingCollectionIdData}>
                  Cancel
                </Button>
              </Group>
            </Box.Content>
          </Box>
        </Overlay>
        <Divider className={styles.blockDividerDivider} />
      </div>
    </div>
  );
};
